import React, { useEffect, useState, Fragment } from 'react';
import { CheckIcon, XIcon, EyeIcon, ShieldCheckIcon, SaveIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { Role, Permission, PermissionLevel } from '../../types';
export function Permissions() {
  const {
    roles,
    permissions,
    getPermissionsByRole,
    updatePermissions
  } = useData();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<Record<string, Permission>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const modules = [
  // --- Panel de Control ---
  {
    key: 'dashboard',
    label: 'Panel de Control (General)',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_calendar',
    label: '↳ Calendario y Citas del Día',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_stats',
    label: '↳ Estadísticas y Gráficos',
    section: 'Panel de Control'
  }, {
    key: 'dashboard_pipeline',
    label: '↳ Pipeline de Ingresos',
    section: 'Panel de Control'
  },
  // --- Módulos Principales ---
  {
    key: 'patients',
    label: 'Pacientes',
    section: 'Módulos Principales'
  }, {
    key: 'doctors',
    label: 'Doctores',
    section: 'Módulos Principales'
  }, {
    key: 'appointments',
    label: 'Citas',
    section: 'Módulos Principales'
  }, {
    key: 'clinicalHistory',
    label: 'Historial Clínico',
    section: 'Módulos Principales'
  },
  // --- Servicios ---
  {
    key: 'services',
    label: 'Servicios (Gestión)',
    section: 'Servicios'
  }, {
    key: 'services_stats',
    label: '↳ Estadísticas y Reportes',
    section: 'Servicios'
  },
  // --- Catálogos ---
  {
    key: 'insurance',
    label: 'Seguros',
    section: 'Catálogos'
  }, {
    key: 'specialties',
    label: 'Especialidades',
    section: 'Catálogos'
  },
  // --- CRM ---
  {
    key: 'contacts',
    label: 'Contactos',
    section: 'CRM'
  }, {
    key: 'companies',
    label: 'Empresas',
    section: 'CRM'
  }, {
    key: 'opportunities',
    label: 'Oportunidades',
    section: 'CRM'
  },
  // --- Seguimiento y Auditoría ---
  {
    key: 'followUpNotes',
    label: 'Notas de Seguimiento',
    section: 'Seguimiento y Auditoría'
  }, {
    key: 'auditLog',
    label: 'Bitácora',
    section: 'Seguimiento y Auditoría'
  },
  // --- Configuración ---
  {
    key: 'settings',
    label: 'Configuración (General)',
    section: 'Configuración'
  }, {
    key: 'settings_profile',
    label: '↳ Perfil',
    section: 'Configuración'
  }, {
    key: 'settings_preferences',
    label: '↳ Preferencias',
    section: 'Configuración'
  }, {
    key: 'settings_notifications',
    label: '↳ Notificaciones',
    section: 'Configuración'
  }, {
    key: 'settings_security',
    label: '↳ Seguridad',
    section: 'Configuración'
  }, {
    key: 'settings_email',
    label: '↳ Correos',
    section: 'Configuración'
  }, {
    key: 'settings_users',
    label: '↳ Usuarios',
    section: 'Configuración'
  }, {
    key: 'settings_roles_permissions',
    label: '↳ Roles y Permisos',
    section: 'Configuración'
  }];
  // Group modules by section for rendering
  const groupedModules = modules.reduce((acc, module) => {
    const section = module.section || 'Otros';
    if (!acc[section]) acc[section] = [];
    acc[section].push(module);
    return acc;
  }, {} as Record<string, typeof modules>);
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.displayName
  }));
  useEffect(() => {
    if (selectedRoleId) {
      const perms = getPermissionsByRole(selectedRoleId);
      const permsMap: Record<string, Permission> = {};
      if (perms.length > 0) {
        perms.forEach(p => {
          permsMap[p.module] = p;
        });
        // Also initialize any modules that are NOT in the loaded data
        // (e.g., newly added settings_* sub-modules)
        modules.forEach(m => {
          if (!permsMap[m.key]) {
            permsMap[m.key] = {
              id: crypto.randomUUID(),
              roleId: selectedRoleId,
              module: m.key,
              canCreate: false,
              canRead: false,
              canUpdate: false,
              canDelete: false,
              permissionLevel: 'none' as PermissionLevel,
              createdAt: new Date(),
              updatedAt: new Date()
            };
          }
        });
      } else {
        // Initialize default permissions for this role (all modules = none)
        modules.forEach(m => {
          permsMap[m.key] = {
            id: crypto.randomUUID(),
            roleId: selectedRoleId,
            module: m.key,
            canCreate: false,
            canRead: false,
            canUpdate: false,
            canDelete: false,
            permissionLevel: 'none' as PermissionLevel,
            createdAt: new Date(),
            updatedAt: new Date()
          };
        });
      }
      setRolePermissions(permsMap);
      setHasChanges(false);
    }
  }, [selectedRoleId, permissions]);
  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-1694" />;
      case 'read':
        return <EyeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-1695" />;
      case 'own':
        return <ShieldCheckIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" data-id="element-1696" />;
      case 'none':
        return <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-1697" />;
    }
  };
  const getPermissionLabel = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return 'Completo';
      case 'read':
        return 'Solo lectura';
      case 'own':
        return 'Propias';
      case 'none':
        return 'Sin acceso';
    }
  };
  const cyclePermission = (module: string) => {
    const levels: PermissionLevel[] = ['none', 'read', 'own', 'full'];
    const currentLevel = rolePermissions[module]?.permissionLevel || 'none';
    const currentIndex = levels.indexOf(currentLevel);
    const nextIndex = (currentIndex + 1) % levels.length;
    const nextLevel = levels[nextIndex];
    const canCreate = nextLevel === 'full';
    const canRead = nextLevel !== 'none';
    const canUpdate = nextLevel === 'full' || nextLevel === 'own';
    const canDelete = nextLevel === 'full';
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...(prev[module] || {
          id: crypto.randomUUID(),
          roleId: selectedRoleId,
          module,
          createdAt: new Date(),
          updatedAt: new Date()
        }),
        permissionLevel: nextLevel,
        canCreate,
        canRead,
        canUpdate,
        canDelete
      }
    }));
    setHasChanges(true);
  };
  // Sync permissions to localStorage in the format getRolePermissions() expects
  const syncPermissionsToLocalStorage = (roleName: string, permsMap: Record<string, Permission>) => {
    try {
      // Load existing permissions map
      const saved = localStorage.getItem('savidna-role-permissions');
      const allPermissions: Record<string, Record<string, PermissionLevel>> = saved ? JSON.parse(saved) : {};
      // Update permissions for this role name across all modules
      modules.forEach(m => {
        if (!allPermissions[m.key]) {
          allPermissions[m.key] = {};
        }
        allPermissions[m.key][roleName] = permsMap[m.key]?.permissionLevel || 'none';
      });
      localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermissions));
      console.log('✅ Permisos sincronizados para rol:', roleName, allPermissions);
    } catch (e) {
      console.error('Error syncing permissions:', e);
    }
  };
  const handleSave = async () => {
    if (!selectedRoleId) return;
    try {
      const permissionsToUpdate = Object.values(rolePermissions);
      await updatePermissions(selectedRoleId, permissionsToUpdate);
      // Find the role name to sync with the permission system (localStorage for sidebar)
      const selectedRole = roles.find(r => r.id === selectedRoleId);
      if (selectedRole) {
        syncPermissionsToLocalStorage(selectedRole.name, rolePermissions);
      }
      setHasChanges(false);
      setSaveMessage('✅ Permisos guardados en Supabase correctamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSaveMessage('Error al guardar permisos');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };
  const selectedRole = roles.find(r => r.id === selectedRoleId);
  return <div className="space-y-6" data-id="element-1698">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1699">
        <div data-id="element-1700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1701">
            <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-1702" />
            Permisos
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1703">
            Configura los permisos de acceso para cada rol
          </p>
        </div>
        {hasChanges && <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1705" />} onClick={handleSave} data-id="element-1704">
            Guardar Cambios
          </Button>}
      </div>

      {saveMessage && <div className={`p-3 rounded-lg text-sm ${saveMessage.includes('Error') ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400'}`} data-id="element-1706">
          {saveMessage}
        </div>}

      {/* Role Selector */}
      <Card data-id="element-1707">
        <div className="p-4" data-id="element-1708">
          <Select label="Seleccionar Rol" options={roleOptions} value={selectedRoleId} onChange={setSelectedRoleId} placeholder="Seleccione un rol para configurar sus permisos" data-id="element-1709" />
          {selectedRole && <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg" data-id="element-1710">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1711">
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-1712">
                  {selectedRole.displayName}:
                </span>{' '}
                {selectedRole.description || 'Sin descripción'}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" data-id="element-1713">
                Nombre interno:{' '}
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-1714">
                  {selectedRole.name}
                </code>{' '}
                — Los usuarios con este rol verán solo los módulos permitidos.
              </p>
            </div>}
        </div>
      </Card>

      {selectedRoleId && <>
          {/* Legend */}
          <Card data-id="element-1715">
            <div className="p-4" data-id="element-1716">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" data-id="element-1717">
                Haz clic en el nivel de acceso para cambiarlo
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1718">
                <div className="flex items-center gap-2" data-id="element-1719">
                  <CheckIcon className="w-5 h-5 text-green-500" data-id="element-1720" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1721">
                    Acceso completo
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1722">
                  <EyeIcon className="w-5 h-5 text-blue-500" data-id="element-1723" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1724">
                    Solo lectura
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1725">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-500" data-id="element-1726" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1727">
                    Solo propias
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1728">
                  <XIcon className="w-5 h-5 text-red-500" data-id="element-1729" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1730">
                    Sin acceso
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Permissions Table - Grouped by Section */}
          <Card data-id="element-1731">
            <div className="overflow-x-auto" data-id="element-1732">
              <table className="w-full" data-id="element-1733">
                <thead data-id="element-1734">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1735">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1736">
                      Módulo
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1737">
                      Nivel de Acceso
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1738">
                      Crear
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1739">
                      Leer
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1740">
                      Editar
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1741">
                      Eliminar
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1742">
                  {Object.entries(groupedModules).map(([sectionName, sectionModules]) => <Fragment key={sectionName} data-id="element-1743">
                        {/* Section Header */}
                        <tr data-id="element-1744">
                          <td colSpan={6} className="px-4 pt-5 pb-2" data-id="element-1745">
                            <span className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400" style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#109888',
                      backgroundColor: '#ffffff'
                    }} data-id="element-1746">
                              {sectionName}
                            </span>
                          </td>
                        </tr>
                        {sectionModules.map((module, index) => {
                  const permission = rolePermissions[module.key];
                  const level = permission?.permissionLevel || 'none';
                  const isSubModule = module.label.startsWith('↳');
                  return <tr key={module.key} className={`border-b border-gray-100 dark:border-gray-800 ${index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/20'}`} data-id="element-1747">
                              <td className={`p-4 font-medium text-gray-900 dark:text-white ${isSubModule ? 'pl-8 text-sm text-gray-600 dark:text-gray-400' : ''}`} data-id="element-1748">
                                {module.label}
                              </td>
                              <td className="p-4 text-center" data-id="element-1749">
                                <button onClick={() => cyclePermission(module.key)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title={`Click para cambiar - Actual: ${getPermissionLabel(level)}`} data-id="element-1750">
                                  {getPermissionIcon(level)}
                                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1751">
                                    {getPermissionLabel(level)}
                                  </span>
                                </button>
                              </td>
                              <td className="p-4 text-center" data-id="element-1752">
                                {permission?.canCreate ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1753" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1754" />}
                              </td>
                              <td className="p-4 text-center" data-id="element-1755">
                                {permission?.canRead ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1756" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1757" />}
                              </td>
                              <td className="p-4 text-center" data-id="element-1758">
                                {permission?.canUpdate ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1759" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1760" />}
                              </td>
                              <td className="p-4 text-center" data-id="element-1761">
                                {permission?.canDelete ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1762" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1763" />}
                              </td>
                            </tr>;
                })}
                      </Fragment>)}
                </tbody>
              </table>
            </div>
          </Card>
        </>}

      {!selectedRoleId && <Card data-id="element-1764">
          <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1765">
            <ShieldCheckIcon className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-1766" />
            <p data-id="element-1767">Selecciona un rol para configurar sus permisos</p>
          </div>
        </Card>}
    </div>;
}